import React, { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Box, LinearProgress, Paper, SxProps, Typography } from '@mui/material';
import ErrorBoundary from './ErrorBoundary';

/**
 * A styled header component using Material-UI's `Box` and `styled` utility.
 * Applies responsive styles for layout, spacing, and alignment.
 */
const StyledHeader = styled(Box, { label: 'PageHeaderBox' })(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: {
    paddingBottom: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    '& > *': {
      flexBasis: '100%',
    },
  },
}));

/**
 * A styled box component using Material-UI's `Box` and `styled` utility.
 * Provides a flexible container with a column layout and minimum height.
 */
const StyledBox = styled(Box, {
  label: 'PageContentBox',
})(() => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
}));

export type Props = {
  title: string;
  loading: boolean;
  children: ReactNode;
  actionComponent?: ReactNode;
  className?: string;
  sx?: SxProps;
};

/**
 * Renders a basic page for consistent styling
 * @param title
 * @param loading
 * @param children
 * @param actionComponent
 * @param className
 * @param sx
 * @constructor
 */
const Page = ({
  title,
  loading,
  children,
  actionComponent,
  className,
  sx,
}: Props) => {
  return (
    <Paper
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {loading && (
        <LinearProgress
          data-testid={'LinearProgress'}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2,
          }}
        />
      )}
      <StyledHeader>
        <Typography component='h2' variant='h6' sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {actionComponent}
      </StyledHeader>
      <StyledBox>
        <ErrorBoundary>{children}</ErrorBoundary>
      </StyledBox>
    </Paper>
  );
};

export default Page;
